/**
 * DashboardMetrics.jsx — AOS Shared Dashboard Primitives
 * Generic, token-driven KPI components reused across People, Finance,
 * Academics, Hostel and future domain dashboards.
 *
 * Consumers:
 *   - /components/hostel/HostelMetrics.jsx   (re-exports for backward compat)
 *   - /pages/ClarosPeopleHome.jsx            (KPI-02A People Dashboard)
 *   - future: Finance, Academics, Assessment
 *
 * No domain-specific logic lives here.
 */
import React from "react";
import { ChevronRight } from "lucide-react";
import { Badge } from "../ui/badge";

// ── Color variant palette ─────────────────────────────────────────────────────
export const METRIC_VARIANTS = {
  blue: {
    grad:   "from-blue-600 to-blue-700",
    ring:   "ring-blue-400/20",
    iconBg: "bg-blue-500/25",
  },
  green: {
    grad:   "from-emerald-500 to-emerald-700",
    ring:   "ring-emerald-400/20",
    iconBg: "bg-emerald-500/25",
  },
  amber: {
    grad:   "from-amber-500 to-amber-600",
    ring:   "ring-amber-400/20",
    iconBg: "bg-amber-500/25",
  },
  purple: {
    grad:   "from-purple-600 to-purple-700",
    ring:   "ring-purple-400/20",
    iconBg: "bg-purple-500/25",
  },
  neutral: {
    grad:   "from-slate-600 to-slate-700",
    ring:   "ring-slate-400/20",
    iconBg: "bg-slate-500/25",
  },
  red: {
    grad:   "from-rose-600 to-rose-700",
    ring:   "ring-rose-400/20",
    iconBg: "bg-rose-500/25",
  },
  teal: {
    grad:   "from-teal-500 to-teal-700",
    ring:   "ring-teal-400/20",
    iconBg: "bg-teal-500/25",
  },
};

// ── MetricCard ────────────────────────────────────────────────────────────────
/**
 * Full-bleed colorful gradient KPI tile.
 * @prop {string}   variant       - Key of METRIC_VARIANTS
 * @prop {string}   label         - Eyebrow label
 * @prop {*}        value         - Primary metric value
 * @prop {string}   sub           - Sub-caption below the value
 * @prop {ReactNode} icon         - Icon element (white Lucide icon recommended)
 * @prop {number}   [occupancyPct] - 0–100; renders visual progress bar when set
 */
export function MetricCard({
  label, value, sub, icon, variant = "blue",
  occupancyPct, "data-testid": testid,
}) {
  const v = METRIC_VARIANTS[variant] || METRIC_VARIANTS.blue;
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl bg-gradient-to-br ${v.grad} p-5 shadow-lg ring-1 ${v.ring} transition-transform hover:-translate-y-0.5`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/65 leading-none truncate">
            {label}
          </p>
          <p className="text-3xl font-bold text-white mt-1.5 leading-none tabular-nums">
            {value}
          </p>
          <p className="text-xs text-white/50 mt-1.5 leading-snug">{sub}</p>
        </div>
        <div className={`shrink-0 p-2.5 rounded-xl ${v.iconBg}`}>{icon}</div>
      </div>

      {occupancyPct !== undefined && (
        <div className="mt-3.5 space-y-1">
          <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-white/70 transition-all duration-700 ease-out"
              style={{ width: `${Math.min(Math.max(occupancyPct, 0), 100)}%` }}
            />
          </div>
          <p className="text-[10px] text-white/45">{occupancyPct}% utilisation</p>
        </div>
      )}
    </div>
  );
}

// ── MetricCardGrid ────────────────────────────────────────────────────────────
/** Responsive 2-col (mobile) / 4-col (≥md) grid wrapper for MetricCards. */
export function MetricCardGrid({ children }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {children}
    </div>
  );
}

// ── PremiumHeroBanner ─────────────────────────────────────────────────────────
/**
 * Dark gradient hero section for module/domain dashboards.
 * @prop {string}    title       - Primary heading
 * @prop {string}    subtitle    - Descriptive sub-line (institution · context)
 * @prop {string}    [demoNotice] - Optional DEMO/SYNTHETIC note (small print)
 * @prop {string}    [badgeLabel] - Optional colored badge (e.g. "HR Dashboard")
 * @prop {ReactNode} [icon]      - Icon shown before the title
 * @prop {ReactNode} [children]  - Renders in right-hand action slot
 */
export function PremiumHeroBanner({
  title, subtitle, demoNotice, badgeLabel, badgeVariant = "blue", icon, children,
}) {
  const badgeCls = badgeVariant === "amber"
    ? "bg-amber-500/20 text-amber-300 border-amber-400/30"
    : "bg-blue-500/20 text-blue-300 border-blue-400/30";
  return (
    <div className="rounded-2xl bg-gradient-to-r from-slate-800 via-slate-800 to-slate-900 border border-slate-700/50 px-6 py-5 shadow-lg">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center flex-wrap gap-2 mb-1.5">
            {icon && <span className="shrink-0">{icon}</span>}
            <h1 className="text-2xl font-bold text-white tracking-tight">{title}</h1>
            {badgeLabel && (
              <Badge className={`${badgeCls} text-xs font-semibold`}>
                {badgeLabel}
              </Badge>
            )}
            <Badge className="bg-slate-600/40 text-slate-400 border-slate-500/30 text-[10px] font-mono uppercase tracking-wider px-2">
              SYNTHETIC_DEMO
            </Badge>
          </div>
          <p className="text-slate-300 text-sm leading-snug">{subtitle}</p>
          {demoNotice && (
            <p className="text-xs text-slate-500 mt-0.5 leading-snug">{demoNotice}</p>
          )}
        </div>
        {children && <div className="shrink-0">{children}</div>}
      </div>
    </div>
  );
}

// ── DashboardPanel ─────────────────────────────────────────────────────────────
/**
 * Light-background card with optional title bar and action slot.
 * Uses AOS light-shell tokens (bg-white, border-border, shadow-sm).
 */
export function DashboardPanel({
  title, icon, action, children,
  "data-testid": testid, className = "",
}) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl bg-white border border-border shadow-sm overflow-hidden ${className}`}
    >
      {(title || action) && (
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/60">
          {title && (
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              {icon}
              {title}
            </div>
          )}
          {action}
        </div>
      )}
      <div className="p-4 md:p-5">{children}</div>
    </div>
  );
}

// ── QuickActionGrid ───────────────────────────────────────────────────────────
/**
 * 2×N grid of icon-led navigation buttons (light background).
 * @prop {Array<{ label, icon, onClick, testid, iconBg? }>} actions
 */
export function QuickActionGrid({ actions }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {actions.map((a, idx) => (
        <button
          key={idx}
          data-testid={a.testid}
          onClick={a.onClick}
          className="group flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1765d1]/40"
        >
          <div
            className={`shrink-0 p-2 rounded-lg ${a.iconBg || "bg-slate-100"} group-hover:scale-110 transition-transform`}
          >
            {a.icon}
          </div>
          <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors flex-1 leading-snug">
            {a.label}
          </span>
          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors shrink-0" />
        </button>
      ))}
    </div>
  );
}
